import { Alert, Button, Divider, Group, Modal, SegmentedControl, Stack, Switch, Text } from '@mantine/core'
import { useEffect, useState } from 'react'
import { TbInfoCircle } from 'react-icons/tb'
import type { BulkEditChanges } from '../lib/bulkEditUsers'

interface UserBulkEditModalProps {
  opened: boolean
  onClose: () => void
  count: number
  isSubmitting: boolean
  onApply: (changes: BulkEditChanges) => void
}

/**
 * Bulk editor for the shared, non-unique user fields. Each field is opt-in:
 * the "Apply this change" switch decides whether the field is written to the
 * selected users — unchecked fields keep each user's existing value.
 */
export function UserBulkEditModal({ opened, onClose, count, isSubmitting, onApply }: UserBulkEditModalProps) {
  const [applyStatus, setApplyStatus] = useState(false)
  const [applyOTP, setApplyOTP] = useState(false)
  const [applyApprover, setApplyApprover] = useState(false)

  const [statusValue, setStatusValue] = useState<'active' | 'inactive'>('active')
  const [otpValue, setOtpValue] = useState(false)
  const [approverValue, setApproverValue] = useState(false)

  // Reset the form each time the modal opens.
  useEffect(() => {
    if (opened) {
      setApplyStatus(false)
      setApplyOTP(false)
      setApplyApprover(false)
      setStatusValue('active')
      setOtpValue(false)
      setApproverValue(false)
    }
  }, [opened])

  const nothingSelected = !applyStatus && !applyOTP && !applyApprover

  const handleApply = () => {
    const changes: BulkEditChanges = {}
    if (applyStatus) changes.isActive = statusValue === 'active'
    if (applyOTP) changes.isWithoutOTP = otpValue
    if (applyApprover) changes.isApprover = approverValue
    onApply(changes)
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Text fw={700} size="lg">
          Bulk Edit Users
        </Text>
      }
      radius="md"
      size="lg"
      overlayProps={{ backgroundOpacity: 0.55, blur: 3 }}
    >
      <Stack gap="md">
        <Alert variant="light" color="blue" icon={<TbInfoCircle size={18} />} radius="md">
          Changes apply to <b>{count}</b> selected user{count > 1 ? 's' : ''}. Only fields you enable below are changed
          — everything else keeps each user's current value.
        </Alert>

        {/* Status */}
        <Stack gap="xs">
          <Switch
            label="Status"
            description="Set account active or inactive"
            checked={applyStatus}
            onChange={(e) => setApplyStatus(e.currentTarget.checked)}
          />
          {applyStatus && (
            <SegmentedControl
              value={statusValue}
              onChange={(v) => setStatusValue(v as 'active' | 'inactive')}
              data={[
                { value: 'active', label: 'Active' },
                { value: 'inactive', label: 'Inactive' },
              ]}
              radius="md"
              size="sm"
            />
          )}
        </Stack>

        <Divider />

        {/* Without OTP */}
        <Stack gap="xs">
          <Switch
            label="Without OTP"
            description="Bypass login OTP verification"
            checked={applyOTP}
            onChange={(e) => setApplyOTP(e.currentTarget.checked)}
          />
          {applyOTP && (
            <SegmentedControl
              value={otpValue ? 'enabled' : 'disabled'}
              onChange={(v) => setOtpValue(v === 'enabled')}
              data={[
                { value: 'enabled', label: 'Enabled' },
                { value: 'disabled', label: 'Disabled' },
              ]}
              radius="md"
              size="sm"
            />
          )}
        </Stack>

        <Divider />

        {/* Approver */}
        <Stack gap="xs">
          <Switch
            label="Approver"
            description="Grant approver privileges"
            checked={applyApprover}
            onChange={(e) => setApplyApprover(e.currentTarget.checked)}
          />
          {applyApprover && (
            <SegmentedControl
              value={approverValue ? 'enabled' : 'disabled'}
              onChange={(v) => setApproverValue(v === 'enabled')}
              data={[
                { value: 'enabled', label: 'Enabled' },
                { value: 'disabled', label: 'Disabled' },
              ]}
              radius="md"
              size="sm"
            />
          )}
        </Stack>

        <Group justify="flex-end" mt="lg">
          <Button variant="default" radius="md" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            radius="md"
            variant="gradient"
            gradient={{ from: '#2563EB', to: '#7C3AED', deg: 135 }}
            loading={isSubmitting}
            disabled={nothingSelected}
            onClick={handleApply}
          >
            Apply to {count} user{count > 1 ? 's' : ''}
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}
